import type { RouteLine } from '../../types/climb';

/**
 * Width of the clear band around a route line, as a fraction of the image's
 * long edge — the same "long edge" convention HoldsOverlay uses for its stroke,
 * so a line and a circle drawn on the same photo stay visually in scale.
 */
export const DEFAULT_ROUTE_CORRIDOR = 0.12;
export const MIN_ROUTE_CORRIDOR = 0.02;
export const MAX_ROUTE_CORRIDOR = 0.5;

/**
 * How thick the line is drawn, as a fraction of the long edge. The default is
 * the width every line was drawn at before it could be set, so an old line
 * keeps the look it was authored with.
 *
 * Thin on purpose indoors: the wall going grey around it is what says where the
 * route is, and the line only has to trace it. On rock there is no such band —
 * the line *is* the topo — so a crag's lines are often wanted heavier, which is
 * what this is for.
 */
export const DEFAULT_ROUTE_THICKNESS = 0.005;
export const MIN_ROUTE_THICKNESS = 0.002;
export const MAX_ROUTE_THICKNESS = 0.02;

/**
 * How much heavier a line is drawn on a thumbnail. The full-size fraction
 * resolves to well under a pixel on a card, where the line disappears and only
 * the corridor is left to read — so proportion is traded for being visible at
 * all, whatever thickness the author chose.
 */
export const COMPACT_LINE_SCALE = 4;

/** A line needs two points; one is a dot, and nothing can be drawn from it. */
export const MIN_ROUTE_LINE_POINTS = 2;

/**
 * How solid the line is drawn. It marks where the route goes on a photo of the
 * wall — it is not the subject of the picture — so it stays a hint rather than
 * ink over it. Shared by the SVG overlay and the canvas renderer below.
 */
export const LINE_OPACITY = 0.5;

/** Whether a line is complete enough to draw or save. */
export function isDrawableLine(line: RouteLine | null | undefined): line is RouteLine {
  return !!line && line.points.length >= MIN_ROUTE_LINE_POINTS;
}

export function clampCorridor(corridor: number): number {
  return Math.min(MAX_ROUTE_CORRIDOR, Math.max(MIN_ROUTE_CORRIDOR, corridor));
}

/**
 * Whether this line asks for the wall around it to be set back at all. Zero is
 * a real answer — "draw the line and leave the photo alone" — and the only one
 * a crag topo wants, so it is stored rather than decided by each renderer.
 */
export function hasCorridor(line: RouteLine | null | undefined): boolean {
  return !!line && (line.corridor ?? 0) > 0;
}

export function clampThickness(thickness: number): number {
  return Math.min(MAX_ROUTE_THICKNESS, Math.max(MIN_ROUTE_THICKNESS, thickness));
}

/** The fraction of the long edge this line is stroked at, at the given size. */
export function lineThickness(
  line: RouteLine | null | undefined,
  marks: 'full' | 'compact' = 'full'
): number {
  const authored = line?.thickness ? clampThickness(line.thickness) : DEFAULT_ROUTE_THICKNESS;

  return marks === 'compact' ? authored * COMPACT_LINE_SCALE : authored;
}

interface Point { x: number; y: number }

/** Tap radius for grabbing a line vertex, as a fraction of the image's long edge. */
export const LINE_HANDLE_HIT = 0.03;

/**
 * How close a press must land on the line to grab it and bend it, rather than
 * add a point at the end. Kept under LINE_HANDLE_HIT so an existing vertex
 * always wins a press that could be read as either.
 */
export const LINE_SEGMENT_HIT = 0.025;

/** Shortest distance from a point to a segment, all in image pixel space. */
export function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  // A zero-length segment is just its endpoint.
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  return Math.hypot(p.x - cx, p.y - cy);
}

/**
 * Index of the vertex under `point`, or -1. Points are the stored fractions;
 * `point` and the result's frame of reference are image pixels. `scale` is the
 * editor's zoom, which shrinks the threshold so the grab area stays the same
 * size on screen however far in the setter has zoomed.
 */
export function lineVertexAt(
  points: Point[],
  point: Point,
  width: number,
  height: number,
  scale = 1
): number {
  const threshold = (Math.max(width, height) * LINE_HANDLE_HIT) / Math.max(1, scale);
  let best = -1;
  let bestDistance = threshold;

  points.forEach((p, index) => {
    const distance = Math.hypot(point.x - p.x * width, point.y - p.y * height);
    if (distance <= bestDistance) {
      best = index;
      bestDistance = distance;
    }
  });

  return best;
}

/**
 * Index to insert a vertex at when the press lands on the line itself, or -1
 * when it lands on the wall and the point should extend the line instead.
 */
export function lineSegmentAt(
  points: Point[],
  point: Point,
  width: number,
  height: number,
  scale = 1
): number {
  if (points.length < 2) return -1;

  const threshold = (Math.max(width, height) * LINE_SEGMENT_HIT) / Math.max(1, scale);
  const toPixels = (p: Point) => ({ x: p.x * width, y: p.y * height });

  for (let i = 0; i < points.length - 1; i++) {
    if (distanceToSegment(point, toPixels(points[i]), toPixels(points[i + 1])) <= threshold) return i + 1;
  }

  return -1;
}
interface CubicSegment { c1: Point; c2: Point; to: Point }

/**
 * The line as cubic segments in a box of the given size.
 *
 * The vertices are smoothed with Catmull-Rom tangents: a route is a continuous
 * line on the wall, and left as a polyline a traverse reads as a folded polygon
 * rather than a path someone could climb. Two points stay a straight segment —
 * there is no curve to infer from them.
 */
export function routeLineCurves(points: Point[], width: number, height: number): { start: Point; segments: CubicSegment[] } | null {
  const p = points.map((v) => ({ x: v.x * width, y: v.y * height }));
  if (p.length === 0) return null;

  const segments: CubicSegment[] = [];
  for (let i = 0; i < p.length - 1; i++) {
    // The segment's neighbours give its tangents; the ends borrow their own
    // point, which flattens the curve into the endpoint instead of overshooting.
    const p0 = p[i - 1] ?? p[i];
    const p1 = p[i];
    const p2 = p[i + 1];
    const p3 = p[i + 2] ?? p2;
    // With only two points there is no neighbour to bend towards, and the
    // formula collapses to the straight segment on its own.
    segments.push({
      c1: { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 },
      c2: { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 },
      to: p2,
    });
  }
  return { start: p[0], segments };
}

/** The same curve as an SVG path in the image's natural pixel space. */
export function routeLinePath(points: Point[], width: number, height: number): string {
  const curve = routeLineCurves(points, width, height);
  if (!curve) return '';
  let d = `M${curve.start.x} ${curve.start.y}`;
  for (const s of curve.segments) {
    d += ` C${s.c1.x} ${s.c1.y}, ${s.c2.x} ${s.c2.y}, ${s.to.x} ${s.to.y}`;
  }
  return d;
}

/**
 * Stroke the line onto a 2D canvas, over an image already drawn at
 * (left, top, width, height). Used by the share-image renderer, which composes
 * on a canvas and so cannot reuse the SVG overlay; the dimming is left out
 * there, because at thumbnail size it would only muddy the picture.
 */
export function strokeRouteLine(
  ctx: CanvasRenderingContext2D,
  line: RouteLine,
  left: number,
  top: number,
  width: number,
  height: number
): void {
  const curve = routeLineCurves(line.points, width, height);
  if (!curve || curve.segments.length === 0) return;

  const longEdge = Math.max(width, height);
  // Whatever the line was drawn at, a half again to keep it in step with the
  // overlay at share-card size, and with a floor so it does not vanish in the
  // small thumbnail box.
  const lineWidth = Math.max(1.5, longEdge * lineThickness(line) * 1.5);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(left + curve.start.x, top + curve.start.y);
  for (const s of curve.segments) {
    ctx.bezierCurveTo(left + s.c1.x, top + s.c1.y, left + s.c2.x, top + s.c2.y, left + s.to.x, top + s.to.y);
  }
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalAlpha = LINE_OPACITY;
  // Halo first, so the line stays legible over pale rock and dark resin alike.
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.lineWidth = lineWidth * 2.4;
  ctx.stroke();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = lineWidth;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Green start, red anchor — the colour semantics holds already use. Full
  // opacity: they are what the line is pointing at.
  const markerRadius = longEdge * 0.022;
  const ends: [Point, string][] = [
    [curve.start, '#22c55e'],
    [curve.segments[curve.segments.length - 1].to, '#ef4444'],
  ];
  for (const [point, color] of ends) {
    ctx.beginPath();
    ctx.arc(left + point.x, top + point.y, markerRadius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = markerRadius * 0.3;
    ctx.stroke();
  }
  ctx.restore();
}

/** Pixel width of the clear band on an image of these natural dimensions. */
export function corridorWidthPx(corridor: number, width: number, height: number): number {
  return clampCorridor(corridor) * Math.max(width, height);
}

/**
 * A CSS `mask-image` data URI that is opaque everywhere except along the line,
 * where it fades to nothing. Applied to the dimming layer it leaves the route
 * corridor untouched and softens the wall around it.
 *
 * The visible part is painted white rather than black on purpose: a CSS mask
 * resolves an image source through its alpha, but the same document read as a
 * luminance mask would invert a black one. White-plus-alpha reads the same
 * either way.
 */
export function routeLineMaskSvg(
  d: string,
  width: number,
  height: number,
  corridorPx: number,
  preserveAspectRatio: string
): string {
  // A soft edge is what makes this read as sfumato and not as a cut-out. Tied
  // to the corridor so a wide band fades over a proportionally wide margin.
  const feather = corridorPx * 0.22;
  // The blur reaches past the stroke's own box, so the filter region has to be
  // grown or the fade would be clipped into a visible straight edge.
  const filterPad = '-25%';
  const filterSize = '150%';

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="${preserveAspectRatio}">` +
      '<defs>' +
        `<filter id="f" x="${filterPad}" y="${filterPad}" width="${filterSize}" height="${filterSize}">` +
          `<feGaussianBlur stdDeviation="${feather}"/>` +
        '</filter>' +
        '<mask id="m">' +
          `<rect width="${width}" height="${height}" fill="white"/>` +
          `<path d="${d}" stroke="black" stroke-width="${corridorPx}" stroke-linecap="round" ` +
            'stroke-linejoin="round" fill="none" filter="url(#f)"/>' +
        '</mask>' +
      '</defs>' +
      `<rect width="${width}" height="${height}" fill="white" mask="url(#m)"/>` +
    '</svg>'
  );
}

/** The same SVG, ready for `mask-image` / `-webkit-mask-image`. */
export function routeLineMaskUrl(
  d: string,
  width: number,
  height: number,
  corridorPx: number,
  preserveAspectRatio: string
): string {
  const svg = routeLineMaskSvg(d, width, height, corridorPx, preserveAspectRatio);
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
